import type { AutomationTriggerType } from '@adweb/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';

import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { apiClient } from '../../lib/api-client';
import { useAuth } from '../auth/auth-context';

type TriggerType = AutomationTriggerType;

interface Org {
  id: string;
  name: string;
  type: 'ADWEB' | 'CLIENT';
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export function CreateAutomationModal({ open, onClose }: Props) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [organizationId, setOrganizationId] = useState('');
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [triggerType, setTriggerType] = useState<TriggerType>('WEBHOOK');
  const [status, setStatus] = useState<'DRAFT' | 'ACTIVE'>('DRAFT');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [formUrl, setFormUrl] = useState('');
  const [chatUrl, setChatUrl] = useState('');
  const [monitoredEmail, setMonitoredEmail] = useState('');
  const [scheduleLabel, setScheduleLabel] = useState('');
  const [estimatedMinutesSavedPerRun, setEstimated] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [slugEdited, setSlugEdited] = useState(false);

  const isPlatform = user?.role === 'SUPER_ADMIN' || user?.role === 'ADWEB_OPERATOR';

  // Inclut l'organisation Adweb-Systèmes — on peut référencer des automatisations
  // pour notre propre exploitation, pas uniquement pour les clients.
  const orgs = useQuery({
    queryKey: ['organizations', 'for-automation'],
    queryFn: async () =>
      (await apiClient.get<{ data: Org[] }>('/organizations?pageSize=100')).data.data,
    enabled: isPlatform && open,
  });

  const mutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        name,
        slug,
        triggerType,
        status,
        ...(description ? { description } : {}),
        ...(category ? { category } : {}),
        ...(isPlatform ? { organizationId } : {}),
        ...(estimatedMinutesSavedPerRun
          ? { estimatedMinutesSavedPerRun: Number(estimatedMinutesSavedPerRun) }
          : {}),
      };
      if (triggerType === 'WEBHOOK') {
        body.webhookUrl = normalizeUrl(webhookUrl);
        if (webhookSecret) body.webhookSecret = webhookSecret;
      } else if (triggerType === 'FORM') {
        body.formUrl = normalizeUrl(formUrl);
      } else if (triggerType === 'SCHEDULED') {
        if (scheduleLabel) body.scheduleLabel = scheduleLabel;
      } else if (triggerType === 'CHAT') {
        if (chatUrl) body.chatUrl = normalizeUrl(chatUrl);
      } else if (triggerType === 'EMAIL') {
        if (monitoredEmail) body.monitoredEmail = monitoredEmail.trim();
      }
      return apiClient.post('/automations', body);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['automations'] });
      reset();
      onClose();
    },
    onError: (err) => {
      const data = (
        err as {
          response?: {
            data?: { message?: string; details?: { fieldErrors?: Record<string, string[]> } };
          };
        }
      ).response?.data;
      const fieldErrors = data?.details?.fieldErrors;
      if (fieldErrors && Object.keys(fieldErrors).length > 0) {
        const formatted = Object.entries(fieldErrors)
          .map(([field, messages]) => `${field} : ${messages.join(', ')}`)
          .join(' • ');
        setError(formatted);
      } else {
        setError(data?.message ?? 'Échec de création');
      }
    },
  });

  function reset() {
    setName('');
    setSlug('');
    setDescription('');
    setCategory('');
    setWebhookUrl('');
    setWebhookSecret('');
    setFormUrl('');
    setChatUrl('');
    setMonitoredEmail('');
    setScheduleLabel('');
    setEstimated('');
    setOrganizationId('');
    setStatus('DRAFT');
    setTriggerType('WEBHOOK');
    setError(null);
    setSlugEdited(false);
  }

  function slugify(value: string): string {
    return value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }

  function handleNameChange(value: string) {
    setName(value);
    // Auto-régénère le slug tant que l'utilisateur ne l'a pas édité manuellement.
    if (!slugEdited) setSlug(slugify(value));
  }

  /** Préfixe https:// si l'URL n'a pas de schéma — évite l'erreur Zod la plus fréquente. */
  function normalizeUrl(value: string): string {
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Référencer une automatisation"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} type="button">
            Annuler
          </Button>
          <Button form="create-automation-form" type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Création…' : 'Référencer'}
          </Button>
        </>
      }
    >
      <form id="create-automation-form" onSubmit={handleSubmit} className="space-y-3" noValidate>
        {error && (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
          >
            {error}
          </div>
        )}

        {isPlatform && (
          <div>
            <label htmlFor="auto-org" className="mb-1 block text-xs font-medium text-slate-700">
              Organisation
            </label>
            <select
              id="auto-org"
              required
              value={organizationId}
              onChange={(e) => setOrganizationId(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="">— choisir une organisation —</option>
              {(orgs.data ?? []).map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                  {o.type === 'ADWEB' ? ' — interne Adweb-Systèmes' : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="auto-name" className="mb-1 block text-xs font-medium text-slate-700">
              Nom
            </label>
            <input
              id="auto-name"
              type="text"
              required
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              placeholder="Génération de factures"
            />
          </div>
          <div>
            <label htmlFor="auto-slug" className="mb-1 block text-xs font-medium text-slate-700">
              Slug
            </label>
            <input
              id="auto-slug"
              type="text"
              required
              minLength={2}
              pattern="[a-z0-9-]+"
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value);
                setSlugEdited(true);
              }}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm"
              placeholder="facturation"
            />
          </div>
        </div>

        <div>
          <label htmlFor="auto-category" className="mb-1 block text-xs font-medium text-slate-700">
            Catégorie (optionnel)
          </label>
          <input
            id="auto-category"
            type="text"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            placeholder="Documents, Leads, Reporting…"
          />
        </div>

        <div>
          <label
            htmlFor="auto-description"
            className="mb-1 block text-xs font-medium text-slate-700"
          >
            Description (optionnel)
          </label>
          <textarea
            id="auto-description"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            placeholder="À quoi sert cette automatisation ?"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="auto-trigger" className="mb-1 block text-xs font-medium text-slate-700">
              Type de déclenchement
            </label>
            <select
              id="auto-trigger"
              value={triggerType}
              onChange={(e) => setTriggerType(e.target.value as TriggerType)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="WEBHOOK">Webhook (déclenchable)</option>
              <option value="FORM">Formulaire (n8n)</option>
              <option value="SCHEDULED">Planifiée (cron)</option>
              <option value="CHAT">Chat (conversationnel)</option>
              <option value="EMAIL">E-mail (entrant)</option>
            </select>
          </div>
          <div>
            <label htmlFor="auto-status" className="mb-1 block text-xs font-medium text-slate-700">
              Statut initial
            </label>
            <select
              id="auto-status"
              value={status}
              onChange={(e) => setStatus(e.target.value as 'DRAFT' | 'ACTIVE')}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="DRAFT">Brouillon</option>
              <option value="ACTIVE">Active</option>
            </select>
          </div>
        </div>

        {triggerType === 'WEBHOOK' && (
          <>
            <div>
              <label
                htmlFor="auto-webhookUrl"
                className="mb-1 block text-xs font-medium text-slate-700"
              >
                URL du webhook n8n (chiffrée en base)
              </label>
              <input
                id="auto-webhookUrl"
                type="url"
                required
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                placeholder="https://n8n.example.test/webhook/..."
              />
            </div>
            <div>
              <label
                htmlFor="auto-webhookSecret"
                className="mb-1 block text-xs font-medium text-slate-700"
              >
                Secret HMAC (optionnel — 32 caractères aléatoires générés si vide)
              </label>
              <input
                id="auto-webhookSecret"
                type="text"
                value={webhookSecret}
                onChange={(e) => setWebhookSecret(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm"
                placeholder="laisser vide pour génération auto"
              />
              <p className="mt-1 text-[11px] text-slate-500">
                Ce secret signera les appels sortants vers n8n et validera les événements entrants
                `/ingest/events`. À reporter dans la config n8n.
              </p>
            </div>
          </>
        )}

        {triggerType === 'FORM' && (
          <div>
            <label htmlFor="auto-formUrl" className="mb-1 block text-xs font-medium text-slate-700">
              URL du formulaire n8n
            </label>
            <input
              id="auto-formUrl"
              type="url"
              required
              value={formUrl}
              onChange={(e) => setFormUrl(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              placeholder="https://n8n.example.test/form/..."
            />
          </div>
        )}

        {triggerType === 'SCHEDULED' && (
          <div>
            <label
              htmlFor="auto-schedule"
              className="mb-1 block text-xs font-medium text-slate-700"
            >
              Planification (libellé informatif)
            </label>
            <input
              id="auto-schedule"
              type="text"
              value={scheduleLabel}
              onChange={(e) => setScheduleLabel(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              placeholder="Tous les lundis 7 h 00"
            />
          </div>
        )}

        {triggerType === 'CHAT' && (
          <div>
            <label htmlFor="auto-chatUrl" className="mb-1 block text-xs font-medium text-slate-700">
              URL du widget de chat (optionnel)
            </label>
            <input
              id="auto-chatUrl"
              type="url"
              value={chatUrl}
              onChange={(e) => setChatUrl(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              placeholder="https://votre-site.test/chat"
            />
            <p className="mt-1 text-[11px] text-slate-500">
              Lien d'ouverture du widget (page publique, embed ou n8n Chat trigger). Si renseigné,
              un bouton « Ouvrir le chat » s'affiche sur la fiche et la liste.
            </p>
          </div>
        )}

        {triggerType === 'EMAIL' && (
          <div>
            <label
              htmlFor="auto-monitoredEmail"
              className="mb-1 block text-xs font-medium text-slate-700"
            >
              Adresse e-mail surveillée (optionnel)
            </label>
            <input
              id="auto-monitoredEmail"
              type="email"
              value={monitoredEmail}
              onChange={(e) => setMonitoredEmail(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              placeholder="factures@entreprise.fr"
            />
            <p className="mt-1 text-[11px] text-slate-500">
              Boîte que ton workflow n8n surveille (déclencheur IMAP / Gmail). Affichée en référence
              sur la fiche pour repérer rapidement quelle automatisation gère quoi.
            </p>
          </div>
        )}

        <div>
          <label htmlFor="auto-estimated" className="mb-1 block text-xs font-medium text-slate-700">
            Temps gagné par exécution (minutes, optionnel)
          </label>
          <input
            id="auto-estimated"
            type="number"
            min="0"
            step="0.5"
            value={estimatedMinutesSavedPerRun}
            onChange={(e) => setEstimated(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            placeholder="ex: 10"
          />
        </div>
      </form>
    </Modal>
  );
}
